/**
 * F23 (Wave 2C) — admin venue "Commission" view (rate history + statements).
 *
 * Renders two sections:
 *   1. Rate history (`AuditLogTimeline` of `venue_commission_rates`)
 *   2. Statements for this venue (a `Table` of recent statements with
 *      View links into the statement detail page)
 *
 * `AdminVenueCommissionTab` is the pure render component — no router params;
 * the consumer passes `venueId`. CST-003 — the F42 rebuild of
 * `AdminVenueDetailPage` dropped the in-page Commission tab, orphaning this
 * component; the routed `AdminVenueCommissionPage` wrapper below (reads
 * `:venueId` from the router) is the remount target — register it in App.tsx
 * at `/venues/:venueId/commission` + add a Commission `DeepLinkCard` on
 * `AdminVenueDetailPage` (both files are owned by the shell track).
 *
 * Pulls data via `adminCommissionApi.rateDetail` + `listStatements({venueId})`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  AuditLogTimeline,
  type AuditEvent,
  Banner,
  Card,
  CurrencyDisplay,
  EmptyState,
  Skeleton,
  StatusChip,
  type StatusChipStatus,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import type { CommissionStatementStatus, VenueCommissionRate } from '@bravemove/types';
import {
  adminCommissionApi,
  type AdminCommissionRateDetailResponse,
  type AdminCommissionStatementRow,
} from '../../../api/commission';
import { translateApiError } from '../../../auth/errors';

const STATUS_CHIP_MAP: Record<CommissionStatementStatus, StatusChipStatus> = {
  draft: 'pending',
  issued: 'in_review',
  settled: 'approved',
  void: 'rejected',
};

function formatPeriod(periodStart: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      timeZone: 'Asia/Beirut',
    }).format(new Date(periodStart));
  } catch {
    return periodStart;
  }
}

function formatRate(bp: number): string {
  return `${(bp / 100).toFixed(2)}%`;
}

function rateRowToEvent(row: VenueCommissionRate): AuditEvent {
  const event: AuditEvent = {
    id: row.id,
    at: row.effectiveAt,
    kind: 'update',
    summary: `Rate set to ${formatRate(row.rateBasisPoints)}`,
    actor: { name: row.setByAdminDisplayName ?? 'System' },
  };
  if (row.reason) {
    (event as { details?: string }).details = row.reason;
  }
  return event;
}

export interface AdminVenueCommissionTabProps {
  readonly venueId: string;
}

/**
 * CST-003 — routed wrapper for the orphaned Commission tab. Reads `:venueId`
 * from the router so App.tsx can mount it with a one-line route registration
 * (`/venues/:venueId/commission`). Renders a header + the pure tab component.
 */
export function AdminVenueCommissionPage() {
  const { t } = useI18n();
  const params = useParams<{ venueId: string }>();
  const venueId = params.venueId ?? '';
  return (
    <div className="space-y-6" data-testid="admin-venue-commission-page">
      <div className="space-y-1">
        <h1 className="text-h1 font-bold text-ink">
          {t('admin:commission.page.venue_tab.title', { defaultValue: 'Commission' })}
        </h1>
        <p className="text-[12.5px] text-ink-3">
          {t('admin:commission.page.venue_tab.deep_link_description', {
            defaultValue: 'Commission rate history + statements for this venue.',
          })}
        </p>
      </div>
      <AdminVenueCommissionTab venueId={venueId} />
    </div>
  );
}

export function AdminVenueCommissionTab({ venueId }: AdminVenueCommissionTabProps) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();

  const [rateData, setRateData] = useState<AdminCommissionRateDetailResponse | null>(null);
  const [statements, setStatements] = useState<readonly AdminCommissionStatementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rate, list] = await Promise.all([
        adminCommissionApi.rateDetail(venueId),
        adminCommissionApi.listStatements({ venueId, perPage: 25 }),
      ]);
      setRateData(rate);
      setStatements(list.data);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [venueId, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchData();
  }, [fetchData]);

  const statementColumns = useMemo<readonly TableColumn<AdminCommissionStatementRow>[]>(
    () => [
      {
        key: 'period',
        header: t('admin:commission.table.statements.column.period', { defaultValue: 'Period' }),
        cell: (row) => (
          <span className="text-[13px] text-ink">{formatPeriod(row.periodStart, locale)}</span>
        ),
      },
      {
        key: 'status',
        header: t('admin:commission.table.statements.column.status', { defaultValue: 'Status' }),
        cell: (row) => (
          <StatusChip
            size="sm"
            status={STATUS_CHIP_MAP[row.status]}
            label={t(`commission:status.${row.status}`, { defaultValue: row.status })}
          />
        ),
      },
      {
        key: 'gross',
        header: t('admin:commission.table.statements.column.gross', {
          defaultValue: 'Gross paid',
        }),
        align: 'end',
        cell: (row) => (
          <CurrencyDisplay amount={row.grossPaidAmountSum} currency="USD" locale={locale} />
        ),
      },
      {
        key: 'commission',
        header: t('admin:commission.table.statements.column.commission', {
          defaultValue: 'Commission',
        }),
        align: 'end',
        cell: (row) => (
          <CurrencyDisplay amount={row.commissionAmountSum} currency="USD" locale={locale} />
        ),
      },
    ],
    [t, locale],
  );

  if (loading) {
    return (
      <div className="flex flex-col gap-4" data-testid="commission-venue-tab-loading">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="commission-venue-tab-error">
        <Banner tone="danger">{error}</Banner>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="commission-venue-tab">
      <Card>
        <div className="flex flex-col gap-3 p-4">
          <h3 className="text-h3 font-bold text-ink">
            {t('admin:commission.page.venue_tab.history_section_title', {
              defaultValue: 'Rate history',
            })}
          </h3>
          {rateData && rateData.history.length > 0 ? (
            <AuditLogTimeline events={rateData.history.map(rateRowToEvent)} />
          ) : (
            <EmptyState
              title={t('admin:commission.page.venue_tab.no_history', {
                defaultValue: 'No rate changes recorded yet.',
              })}
            />
          )}
        </div>
      </Card>

      <Card>
        <div className="flex flex-col gap-3 p-4">
          <h3 className="text-h3 font-bold text-ink">
            {t('admin:commission.page.venue_tab.statements_section_title', {
              defaultValue: 'Statements for this venue',
            })}
          </h3>
          {statements.length === 0 ? (
            <EmptyState
              title={t('admin:commission.page.venue_tab.no_statements', {
                defaultValue: 'No statements for this venue yet.',
              })}
            />
          ) : (
            <Table
              data={statements as AdminCommissionStatementRow[]}
              columns={statementColumns as TableColumn<AdminCommissionStatementRow>[]}
              rowKey={(r) => r.id}
              onRowPress={(row) => navigate(`/commission-statements/${row.id}`)}
            />
          )}
        </div>
      </Card>
    </div>
  );
}

export default AdminVenueCommissionTab;
